import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialSelection, ProposalLineState, QuoteOption } from '../vendor-quote-flow.types';
import { formatMdy } from '../vendor-quote-flow.util';

export interface SelectVendorQuoteDialogData {
  line: ProposalLineState;
  /** Quote items for this material (every quote on the bid that has it). */
  options: QuoteOption[];
  /** Previous selection, when the user comes Back from Compare. */
  selection?: MaterialSelection;
  /** "Include expired quotes" as the user left it, when coming Back from Compare. */
  allowExpired?: boolean;
  /**
   * Where the dialog is opened from. 'purchase-order' picks exactly one quote (a PO line has one
   * vendor) and words it for a PO. Defaults to 'proposal'.
   */
  context?: 'proposal' | 'purchase-order';
}

export interface SelectVendorQuoteDialogResult {
  selection: MaterialSelection;
  allowExpired: boolean;
}

/**
 * Select Vendor Quote — one material (mockup F1–F4).
 * Picking 2+ quotes splits the material into one line per vendor, with a quantity per vendor (F3).
 */
@Component({
  selector: 'app-select-vendor-quote-dialog',
  templateUrl: './select-vendor-quote-dialog.component.html',
  styleUrls: ['./select-vendor-quote-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SelectVendorQuoteDialogComponent {
  line: ProposalLineState;
  options: QuoteOption[];
  selection: MaterialSelection;
  /** Lowest unit price among the quotes that can still be used (F2 "Lowest price"). */
  lowestPrice: number | null = null;
  /** "Include expired quotes" — off by default; the switch only shows when a quote has expired. */
  allowExpired: boolean;
  readonly hasExpired: boolean;
  readonly isPo: boolean;

  constructor(
    private dialogRef: MatDialogRef<SelectVendorQuoteDialogComponent, SelectVendorQuoteDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) data: SelectVendorQuoteDialogData,
    private cd: ChangeDetectorRef,
  ) {
    this.line = data.line;
    this.options = data.options;
    this.selection = new Map(data.selection ?? []);
    this.isPo = data.context === 'purchase-order';
    this.hasExpired = this.options.some(o => o.isExpired);
    this.allowExpired = !!data.allowExpired && this.hasExpired;
    this.updateLowest();
  }

  private updateLowest(): void {
    const usable = this.options.filter(o => this.canPick(o));
    this.lowestPrice = usable.length ? Math.min(...usable.map(o => o.unitPrice)) : null;
  }

  /** Turning it off drops any expired quote already picked. */
  setAllowExpired(on: boolean): void {
    this.allowExpired = on;
    if (!on) {
      for (const opt of this.options) {
        if (opt.isExpired && this.selection.has(opt.optionKey)) this.toggle(opt, false, true);
      }
    }
    this.updateLowest();
    this.cd.markForCheck();
  }

  /** Lines with no price, and expired quotes unless "Include expired quotes" is on, are shown but can't be chosen. */
  canPick(opt: QuoteOption): boolean {
    return (!opt.isExpired || this.allowExpired) && opt.hasPrice;
  }

  blockedReason(opt: QuoteOption): string {
    if (opt.isExpired && !this.allowExpired) return 'This quote has expired. Turn on "Include expired quotes" to use it anyway.';
    if (!opt.hasPrice) return 'This quote line has no price. Edit the quote to add one.';
    if (opt.isExpired) return 'This quote has expired. Confirm the price with the vendor.';
    return '';
  }

  get count(): number {
    return this.selection.size;
  }

  isSelected(opt: QuoteOption): boolean {
    return this.selection.has(opt.optionKey);
  }

  toggle(opt: QuoteOption, checked: boolean, force = false): void {
    if (!force && !this.canPick(opt)) return;
    // On a PO the line has one vendor, so picking a quote replaces the previous pick.
    if (checked && this.isPo) this.selection.clear();
    if (checked) {
      // First pick takes the whole quantity; further picks start at 0 until the user splits it (F3).
      this.selection.set(opt.optionKey, this.selection.size ? 0 : this.line.qty);
    } else {
      this.selection.delete(opt.optionKey);
    }
    if (this.selection.size === 1) {
      const [only] = this.selection.keys();
      this.selection.set(only, this.line.qty);
    }
    this.cd.markForCheck();
  }

  setQty(opt: QuoteOption, raw: string): void {
    this.selection.set(opt.optionKey, Math.max(0, parseInt(raw, 10) || 0));
  }

  qtyFor(opt: QuoteOption): number {
    return this.selection.get(opt.optionKey) ?? 0;
  }

  /** "$X more/less per EA" or "Same price" against what the proposal has now. */
  priceDiff(opt: QuoteOption): { cls: 'up' | 'dn' | ''; text: string } {
    const d = opt.unitPrice - this.line.unitCost;
    if (d === 0) return { cls: '', text: 'Same price' };
    const money = Math.abs(d).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return { cls: d > 0 ? 'up' : 'dn', text: `${money} ${d > 0 ? 'more' : 'less'} per ${opt.unit || 'unit'}` };
  }

  formatDate(value: string): string {
    return formatMdy(value);
  }

  trackByKey(index: number, opt: QuoteOption): string {
    return opt.optionKey;
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  preview(): void {
    if (!this.count) return;
    this.dialogRef.close({ selection: this.selection, allowExpired: this.allowExpired });
  }
}
